import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { BlogPost } from '../../../types/blog';

interface Props {
  post: BlogPost;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

function parseContent(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="mt-10 mb-4" style={{ color: '#4a3728', fontSize: '22px', fontWeight: '700', borderLeft: '4px solid #5a9e6f', paddingLeft: '12px' }}>
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="mt-7 mb-3" style={{ color: '#6b5a4e', fontSize: '18px', fontWeight: '700' }}>
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={i} style={{ color: '#4a3728', fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>
          {line.replace(/\*\*/g, '')}
        </p>
      );
    } else if (line.startsWith('- ')) {
      elements.push(
        <li key={i} className="ml-6 mb-2" style={{ color: '#6b5a4e', fontSize: '16px', lineHeight: '1.8', listStyleType: 'disc' }}>
          {line.replace('- ', '')}
        </li>
      );
    } else if (line.trim() !== '') {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      elements.push(
        <p key={i} className="mb-4" style={{ color: '#6b5a4e', fontSize: '16px', lineHeight: '1.9' }}>
          {parts.map((part, pi) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <strong key={pi} style={{ color: '#4a3728', fontWeight: '700' }}>{part.replace(/\*\*/g, '')}</strong>
            ) : part
          )}
        </p>
      );
    }
    i++;
  }
  return elements;
}

export default function BlogDetailContent({ post }: Props) {
  const LINE_URL = 'https://lin.ee/PNbhQI0';
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    if (!post.category_id) return;
    const fetchRelated = async () => {
      const { data } = await supabase
        .from('8a4f04ed_blog_posts')
        .select('*, category:8a4f04ed_blog_categories(*)')
        .eq('category_id', post.category_id as string)
        .neq('id', post.id)
        .order('published_at', { ascending: false })
        .limit(3);
      if (data) setRelatedPosts(data as BlogPost[]);
    };
    fetchRelated();
  }, [post.id, post.category_id]);

  const cat = post.category;

  return (
    <article className="section-padding" style={{ background: '#fff8f2' }}>
      <div className="content-width">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-8 text-sm flex-wrap" aria-label="パンくず">
            <Link to="/" style={{ color: '#9b8b7f' }} className="hover:text-[#5a9e6f] transition-colors whitespace-nowrap">トップ</Link>
            <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-right-s-line" style={{ color: '#9b8b7f' }}></i></span>
            <Link to="/blog" style={{ color: '#9b8b7f' }} className="hover:text-[#5a9e6f] transition-colors whitespace-nowrap">ブログ</Link>
            <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-right-s-line" style={{ color: '#9b8b7f' }}></i></span>
            <span style={{ color: '#4a3728' }} className="line-clamp-1">{post.title}</span>
          </nav>

          {/* Category & Date */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            {cat && (
              <span className="inline-block px-4 py-1 rounded-full text-sm font-bold" style={{ background: cat.color, color: '#fff' }}>
                {cat.name}
              </span>
            )}
            <span style={{ color: '#9b8b7f', fontSize: '14px' }}>{formatDate(post.published_at)}</span>
            <div className="flex items-center gap-1">
              <span className="flex items-center justify-center w-4 h-4"><i className="ri-time-line text-xs" style={{ color: '#9b8b7f' }}></i></span>
              <span style={{ color: '#9b8b7f', fontSize: '14px' }}>約{post.read_minutes}分で読めます</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="mb-8" style={{ color: '#4a3728', fontSize: '28px', fontWeight: '700', lineHeight: '1.5' }}>
            {post.title}
          </h1>

          {/* Hero Image */}
          {post.thumbnail_url && (
            <div className="w-full h-[360px] md:h-[460px] rounded-xl overflow-hidden mb-10">
              <img
                src={post.thumbnail_url}
                alt={post.title}
                className="w-full h-full object-cover object-top"
                width={800}
                height={460}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          )}

          {/* Author */}
          <div className="flex items-center gap-4 mb-10 p-5 rounded-xl" style={{ background: '#fff', border: '1px solid #e8d8cc' }}>
            <div className="flex items-center justify-center w-12 h-12 rounded-full shrink-0" style={{ background: '#f2f8f4' }}>
              <i className="ri-user-smile-line text-xl" style={{ color: '#5a9e6f' }}></i>
            </div>
            <div>
              <p style={{ color: '#4a3728', fontWeight: '700', fontSize: '16px' }}>{post.author_name}</p>
              <p style={{ color: '#9b8b7f', fontSize: '14px' }}>{post.author_role} / 縁結び 和はは</p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-12">
            {post.content ? parseContent(post.content) : (
              <p style={{ color: '#6b5a4e', fontSize: '16px', lineHeight: '1.9' }}>{post.excerpt}</p>
            )}
          </div>

          {/* Tags */}
          {(() => {
            let tags: string[] = [];
            if (Array.isArray(post.tags)) {
              tags = (post.tags as unknown[]).map(String).filter(Boolean);
            } else if (typeof post.tags === 'string' && post.tags.trim()) {
              tags = post.tags.split(',').map((t) => t.trim()).filter(Boolean);
            }
            if (tags.length === 0) return null;
            return (
              <div className="flex flex-wrap gap-2 mb-12">
                {tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-sm" style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}>
                    #{tag}
                  </span>
                ))}
              </div>
            );
          })()}

          {/* CTA Box */}
          <div className="rounded-2xl p-8 text-center mb-14" style={{ background: '#fff', border: '2px solid #5a9e6f' }}>
            <p className="mb-2 font-bold" style={{ color: '#5a9e6f', fontSize: '14px', letterSpacing: '2px' }}>まずはお気軽にご相談ください</p>
            <h2 className="mb-4" style={{ color: '#4a3728', fontSize: '22px', fontWeight: '700' }}>
              「和はは」のカウンセラーが<br className="hidden sm:block" />あなたの婚活をトータルサポート
            </h2>
            <p className="mb-6" style={{ color: '#6b5a4e', fontSize: '15px', lineHeight: '1.8' }}>
              いつでもLINEで無料相談受付中。<br className="hidden sm:block" />話を聞いてもらうだけでも大丈夫です。
            </p>
            <a href={LINE_URL} target="_blank" rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-white transition-opacity hover:opacity-90 whitespace-nowrap"
              style={{ background: '#06C755', fontSize: '16px' }}>
              <span className="flex items-center justify-center w-6 h-6"><i className="ri-line-fill text-xl"></i></span>
              LINEで無料相談する
            </a>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div>
              <h3 className="mb-6" style={{ color: '#4a3728', fontSize: '20px', fontWeight: '700' }}>関連記事</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <Link key={rp.id} to={`/blog/${rp.id}`}
                    className="block bg-white rounded-xl overflow-hidden cursor-pointer"
                    style={{ transition: 'transform 0.3s' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                  >
                    {rp.thumbnail_url && (
                      <div className="w-full h-[130px] overflow-hidden">
                        <img src={rp.thumbnail_url} alt={rp.title} className="w-full h-full object-cover object-top" width={280} height={130} loading="lazy" />
                      </div>
                    )}
                    <div className="p-4">
                      <p className="line-clamp-2" style={{ color: '#4a3728', fontSize: '14px', fontWeight: '600', lineHeight: '1.6' }}>{rp.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to list */}
          <div className="text-center mt-12">
            <Link to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
              style={{ background: '#fff', color: '#5a9e6f', border: '2px solid #5a9e6f', fontSize: '15px' }}>
              <span className="flex items-center justify-center w-5 h-5"><i className="ri-arrow-left-line"></i></span>
              ブログ一覧に戻る
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
